import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, ShieldCheck, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { ThemeToggle } from '../components/ThemeToggle';

const CODE_LENGTH = 6;
const RESEND_COOLDOWN = 30;

export default function VerifyOTPPage() {
  const navigate = useNavigate();
  const { pendingVerification, verifyOTP, resendOTP, isAuthenticated } = useAuth();
  const [code, setCode] = useState(Array(CODE_LENGTH).fill(''));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(RESEND_COOLDOWN);
  const [resending, setResending] = useState(false);
  const inputRefs = useRef([]);

  const phone = pendingVerification?.phone || '';
  const maskedPhone = pendingVerification?.masked_phone || '***-***-****';

  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => setResendTimer(t => t - 1), 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  useEffect(() => {
    // Only bounce to signup if there's no pending verification AND the user
    // isn't already authenticated. After a successful verify, verifyOTP clears
    // pendingVerification and sets the user, so we must NOT redirect here —
    // handleVerify navigates to the dashboard instead.
    if (!pendingVerification && !isAuthenticated) navigate('/signup');
  }, [pendingVerification, isAuthenticated, navigate]);

  const handleChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);
    setError('');
    if (digit && index < CODE_LENGTH - 1) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) inputRefs.current[index - 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (pasted.length > 0) {
      const newCode = Array(CODE_LENGTH).fill('');
      pasted.split('').forEach((d, i) => { newCode[i] = d; });
      setCode(newCode);
      inputRefs.current[Math.min(pasted.length, CODE_LENGTH) - 1]?.focus();
    }
  };

  const fullCode = code.join('');
  const isComplete = fullCode.length === CODE_LENGTH;

  const handleVerify = async () => {
    if (!isComplete) return;
    setLoading(true);
    setError('');
    const result = await verifyOTP(phone, fullCode);
    if (result.success) {
      toast.success('Phone verified!');
      const products = result.user?.products || [];
      if (products.length > 1) navigate('/select-product');
      else if (products[0] === 'voicedesk') navigate('/voicedesk/dashboard');
      else if (products[0] === 'tele_sale') navigate('/telesales/setup');
      else navigate('/dashboard');
    } else {
      setError(result.error);
      setCode(Array(CODE_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    }
    setLoading(false);
  };

  const handleResend = async () => {
    if (resendTimer > 0 || resending) return;
    setResending(true);
    const result = await resendOTP(phone);
    if (result.success) {
      toast.success('New code sent');
      setResendTimer(RESEND_COOLDOWN);
    } else {
      toast.error(result.error);
    }
    setResending(false);
  };

  if (!pendingVerification) return null;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="verify-otp-page">
      <div className="fixed top-4 right-4 z-50"><ThemeToggle compact /></div>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-lg overflow-hidden shrink-0">
              <img src="/assets/agent9-icon.png" alt="Agent9" className="w-full h-full object-contain" />
            </div>
            <span className="text-xl font-bold text-foreground tracking-tight">Agent9</span>
          </Link>
        </div>

        <Card className="bg-card border-border backdrop-blur-sm">
          <CardContent className="p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <ShieldCheck size={28} className="text-primary" />
            </div>

            <h1 className="text-xl font-bold text-foreground" data-testid="verify-heading">{isAuthenticated ? 'Verify your new phone number' : 'Verify your phone number'}</h1>
            <p className="text-muted-foreground text-sm mt-2">
              We sent a 6-digit code to <span className="text-foreground font-medium">{maskedPhone}</span>
            </p>
            <p className="text-muted-foreground text-xs mt-1">
              We use your verified phone number to secure your account and power voice features.
            </p>

            <div className="flex justify-center gap-2.5 mt-6" onPaste={handlePaste}>
              {code.map((digit, i) => (
                <input
                  key={i}
                  ref={el => inputRefs.current[i] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  className="w-12 h-14 text-center text-xl font-bold bg-muted border border-border rounded-lg text-foreground focus:border-primary focus:ring-1 focus:ring-primary/30 outline-none transition-all"
                  data-testid={`otp-input-${i}`}
                />
              ))}
            </div>

            {error && (
              <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2 mt-4" data-testid="otp-error">
                {error}
              </div>
            )}

            <Button onClick={handleVerify} disabled={!isComplete || loading}
              className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-medium mt-6"
              data-testid="verify-submit-btn">
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-foreground border-t-transparent" />
              ) : 'Verify'}
            </Button>

            <div className="mt-4">
              {resendTimer > 0 ? (
                <p className="text-muted-foreground text-sm">
                  Resend code in <span className="text-foreground font-medium">{resendTimer}s</span>
                </p>
              ) : (
                <button onClick={handleResend} disabled={resending}
                  className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 transition-colors"
                  data-testid="resend-otp-btn">
                  <RotateCcw size={14} />
                  {resending ? 'Sending...' : 'Resend code'}
                </button>
              )}
            </div>

            <div className="mt-6 pt-4 border-t border-border flex items-center justify-center gap-4 text-sm">
              <Link to={isAuthenticated ? '/settings/account' : '/signup'} className="text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1" data-testid="back-to-signup-link">
                <ArrowLeft size={14} /> {isAuthenticated ? 'Back to Account Settings' : 'Back to signup'}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
